import { message, notification } from "antd";
import { trackToast, ToastOutcome } from "services/UsageTrackingService";

/**
 * Track A (qualidade de experiência): intercepta os singletons `notification` e
 * `message` do antd para registrar sucesso/erro percebidos pelo usuário, sem
 * precisar instrumentar cada um dos ~500 call sites.
 *
 * A instrumentação é feita uma única vez, envolvendo os métodos de exibição e
 * repassando a chamada original intacta.
 */

let patched = false;

const OUTCOME_BY_METHOD: Record<string, ToastOutcome> = {
  success: "success",
  error: "error",
  warning: "warning",
  warn: "warning",
  info: "info",
  open: "info",
  loading: "info",
};

function extractNotificationText(args: any[]): string | undefined {
  const cfg = args?.[0];
  if (!cfg) return undefined;
  if (typeof cfg === "string") return cfg;
  const parts = [cfg.message, cfg.description].filter((p) => typeof p === "string");
  return parts.length ? parts.join(" - ") : undefined;
}

function extractMessageText(args: any[]): string | undefined {
  const cfg = args?.[0];
  if (!cfg) return undefined;
  if (typeof cfg === "string") return cfg;
  if (typeof cfg.content === "string") return cfg.content;
  return undefined;
}

function patchChannel(
  channel: "notification" | "message",
  target: Record<string, any>,
  methods: string[],
  extractText: (args: any[]) => string | undefined
) {
  methods.forEach((method) => {
    const original = target[method];
    if (typeof original !== "function") return;

    target[method] = function (this: unknown, ...args: any[]) {
      try {
        let outcome = OUTCOME_BY_METHOD[method] || "info";
        // notification.open / message.open podem carregar o tipo no config
        if (method === "open") {
          const type = args?.[0]?.type;
          if (type && OUTCOME_BY_METHOD[type]) outcome = OUTCOME_BY_METHOD[type];
        }
        trackToast({ outcome, channel, method, text: extractText(args) });
      } catch (_) {
        /* nunca deixar o tracking quebrar o toast */
      }
      return original.apply(this, args);
    };
  });
}

export function initToastTracking() {
  if (patched) return;
  patched = true;

  try {
    patchChannel(
      "notification",
      notification as unknown as Record<string, any>,
      ["success", "error", "warning", "warn", "info", "open"],
      extractNotificationText
    );
    patchChannel(
      "message",
      message as unknown as Record<string, any>,
      ["success", "error", "warning", "warn", "info", "loading", "open"],
      extractMessageText
    );
  } catch (err) {
    console.error("[tracking] initToastTracking failed", err);
  }
}
